import { z } from 'zod';

import { DiscordLabelTextSchema, FieldDescriptionSchema } from './shared.js';

// Field kind enum ---------------------------------------------------------
//
// Maps deterministically to a Discord modal component (see
// .docs/feedback-forms/bot-interactions.md). Adding a kind here means
// extending the bot's form-modal-builder; keep the two in lockstep.
export const FeedbackFormFieldKindSchema = z.enum([
  'short_text',
  'long_text',
  'rating',
  'select',
  'severity',
  'binary',
  // NPS is a fixed-shape kind: question copy is hard-coded, scale is
  // always 1-10, required is forced. Stored as a separate kind so the
  // bot's modal builder can render it with descending order and
  // endpoint suffixes ("10 - Very likely", "1 - Very unlikely") without
  // overloading the rating kind.
  'nps',
]);
export type FeedbackFormFieldKind = z.infer<typeof FeedbackFormFieldKindSchema>;

// Per-kind config discriminated union -------------------------------------
//
// Discriminator is the field's `kind`. The portal builder spreads
// kind + config when sending replace_feedback_form_fields, so the wire
// shape and this union match. Empty configs use z.object({}) rather than
// z.unknown so the union narrows cleanly.

const ShortTextConfigSchema = z.object({}).strict();
const LongTextConfigSchema = z.object({}).strict();
const SeverityConfigSchema = z.object({}).strict();
const NpsConfigSchema = z.object({}).strict();

// Binary fields default to Yes / No. The studio can override either side
// with a custom label (text or emoji). Cap at 32 chars: well under
// Discord's 100-char radio option label limit and short enough to keep
// the modal readable. Stored answer is the canonical 'true' / 'false'
// so a later label rename does not retroactively rewrite history.
export const BINARY_LABEL_MAX = 32;
export const BINARY_TRUE_LABEL_DEFAULT = 'Yes';
export const BINARY_FALSE_LABEL_DEFAULT = 'No';

const BinaryConfigSchema = z
  .object({
    trueLabel: z.string().min(1).max(BINARY_LABEL_MAX).optional(),
    falseLabel: z.string().min(1).max(BINARY_LABEL_MAX).optional(),
  })
  .strict();

// Rating fields are always 1-5 (the 1-10 scale split out into the
// dedicated NPS kind in story 1). The optional `display` flips the
// radio option labels between numbers and a configurable emoji
// repeated n times. Stored value is always the integer 1-5
// regardless of display mode.
export const RatingDisplaySchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('numbers') }),
  // Up to 8 chars to allow multi-codepoint emoji (most ZWJ sequences
  // fit, e.g. "👨‍🚀" = 7 UTF-16 code units).
  z.object({ mode: z.literal('emoji'), emoji: z.string().min(1).max(8) }),
]);
export type RatingDisplay = z.infer<typeof RatingDisplaySchema>;

const RatingConfigSchema = z
  .object({
    display: RatingDisplaySchema.optional(),
  })
  .strict();

const SelectConfigSchema = z
  .object({
    // 1-25 to fit Discord's String Select cap. Empty options would
    // produce a no-op picker, so 1 is the lower bound.
    options: z.array(z.string().min(1).max(100)).min(1).max(25),
    // Discord's modal String Select supports max_values up to 25; 1 keeps
    // the single-select behaviour (the default). Clamped at the option
    // count by the builder so a studio can't ask for more selections
    // than there are choices to make.
    maxValues: z.number().int().min(1).max(25).optional(),
  })
  .strict();

export const FeedbackFormFieldConfigSchema = z.discriminatedUnion('kind', [
  ShortTextConfigSchema.extend({ kind: z.literal('short_text') }),
  LongTextConfigSchema.extend({ kind: z.literal('long_text') }),
  RatingConfigSchema.extend({ kind: z.literal('rating') }),
  SelectConfigSchema.extend({ kind: z.literal('select') }),
  SeverityConfigSchema.extend({ kind: z.literal('severity') }),
  BinaryConfigSchema.extend({ kind: z.literal('binary') }),
  NpsConfigSchema.extend({ kind: z.literal('nps') }),
]);
export type FeedbackFormFieldConfig = z.infer<typeof FeedbackFormFieldConfigSchema>;

// Field (camelCase) -------------------------------------------------------

export const FeedbackFormFieldSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  orderIndex: z.number().int().nonnegative(),
  kind: FeedbackFormFieldKindSchema,
  title: DiscordLabelTextSchema,
  description: FieldDescriptionSchema,
  required: z.boolean(),
  // The DB stores config without the discriminator key, but the portal
  // works with the union shape. Callers that hold a row should compose
  // { kind, ...config } before parsing against FeedbackFormFieldConfigSchema.
  config: z.record(z.unknown()),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type FeedbackFormField = z.infer<typeof FeedbackFormFieldSchema>;

export const FeedbackFormFieldRowSchema = z
  .object({
    id: z.string().uuid(),
    form_id: z.string().uuid(),
    order_index: z.number().int().nonnegative(),
    kind: FeedbackFormFieldKindSchema,
    title: z.string(),
    description: z.string().nullable(),
    required: z.boolean(),
    config: z.record(z.unknown()),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
  })
  .transform((row) => ({
    id: row.id,
    formId: row.form_id,
    orderIndex: row.order_index,
    kind: row.kind,
    title: row.title,
    description: row.description,
    required: row.required,
    config: row.config,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  })) satisfies z.ZodType<FeedbackFormField, z.ZodTypeDef, unknown>;

// Field input shape sent to replace_feedback_form_fields ------------------
//
// Builder hands this array to the RPC. Every variant carries the same
// title/description/required set plus its kind-specific extras; the
// discriminator on `kind` means a misshapen field (e.g. select with no
// options) fails parsing client-side before the RPC ever sees it.

const fieldInputBase = {
  title: DiscordLabelTextSchema,
  description: FieldDescriptionSchema.optional(),
  required: z.boolean().default(false),
} as const;

export const FeedbackFormFieldInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('short_text'), ...fieldInputBase }),
  z.object({ kind: z.literal('long_text'), ...fieldInputBase }),
  z.object({
    kind: z.literal('rating'),
    ...fieldInputBase,
    display: RatingDisplaySchema.optional(),
  }),
  z.object({
    kind: z.literal('select'),
    ...fieldInputBase,
    options: z.array(z.string().min(1).max(100)).min(1).max(25),
    // Optional. 1 (or omitted) keeps the existing single-select
    // behaviour. >1 enables multi-select up to that count, capped at the
    // option list length and Discord's 25-item limit.
    maxValues: z.number().int().min(1).max(25).optional(),
  }),
  z.object({ kind: z.literal('severity'), ...fieldInputBase }),
  z.object({
    kind: z.literal('binary'),
    ...fieldInputBase,
    trueLabel: z.string().min(1).max(BINARY_LABEL_MAX).optional(),
    falseLabel: z.string().min(1).max(BINARY_LABEL_MAX).optional(),
  }),
  // NPS has no kind-specific config and forces required = true via the
  // builder; the input shape still accepts the base fields so the
  // discriminator stays uniform.
  z.object({ kind: z.literal('nps'), ...fieldInputBase }),
]);
export type FeedbackFormFieldInput = z.infer<typeof FeedbackFormFieldInputSchema>;

// Per-form field cap. Discord's Modal V2 caps a modal at 5 top-level
// components (Label + its wrapped input counts as one). The anonymity
// control lives outside the modal (button-prompt before opening) so
// every modal slot can carry a real field; the cap is therefore 5.
export const FORM_FIELD_MAX = 5;

// NPS field defaults. Title is fixed copy that fits inside Discord's
// 45-char Label cap. Description stays null so the Discord modal shows
// only the question; the portal-side NpsConfig component carries the
// "what is NPS" explainer for studios. Required is forced to true via
// the builder (an NPS field with no answer is meaningless).
export const NPS_FIELD_DEFAULTS = {
  title: 'How likely are you to recommend this game?',
  description: null as string | null,
  required: true as const,
};

export const FeedbackFormFieldsInputSchema = z
  .array(FeedbackFormFieldInputSchema)
  .min(1)
  .max(FORM_FIELD_MAX);
export type FeedbackFormFieldsInput = z.infer<typeof FeedbackFormFieldsInputSchema>;
